import { useContext, useState, useEffect, useCallback, useMemo } from "react";
import {
  Box, Typography,
  Fade
} from "@mui/material";
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { PropertyContext } from "../../../../contexts/PropertyContext";
import { KPI_LABEL_MAP, KPI_OPTIONS, responsiveControlStyle } from "../../../../shared/utils/prediction-utils";
import { ChatDatePicker } from "../chat-prediction/ChatDatePicker";
import { PredictionChatbot } from "../chat-prediction";
import useQuery from "../../../../shared/components/hooks/useQuery";
import { PredictionGraph } from "../prediction-graphs";
import { PropertyOption } from "../../../../shared/interfaces/property";
import StyledSelect from "../../../../shared/components/styledSelect";
import EventCalendarModal from "./EventModal";
import StyledButton from "../../../../shared/components/styledButton";

export interface ExtendedPropertyOption extends PropertyOption {
  country?: string;
  state?: string;
  city?: string;
}

const Forecast = () => {
  const { propertyCode } = useContext(PropertyContext);

  const { data: propertiesData, loading: isPropertiesLoading } = useQuery(
    `${process.env.REACT_APP_API_URL}/properties/my`,
    {},
    null,
    { enabled: true }
  );

  const allProperties: ExtendedPropertyOption[] = useMemo(() => {
    const items = (propertiesData as any)?.items || [];
    return items.map((item: any) => ({
      code: String(item.property.code),
      id: item.property.id,
      name: item.property.name,
      country: item.property.country,
      state: item.property.state,
      city: item.property.city
    }));
  }, [propertiesData]);

  const { data: latestDateResponse } = useQuery(
    propertyCode ? `${process.env.REACT_APP_API_URL}/analytics/latest-date/${propertyCode}` : "",
    {},
    null,
    { enabled: !!propertyCode }
  );

  const [selectedPropertyCodes, setSelectedPropertyCodes] = useState<string[]>([]);
  const [selectedKpis, setSelectedKpis] = useState<string[]>(["occupancy", "adr"]);
  const [forecastType, setForecastType] = useState<string>("weekly");
  const [explanationType, setExplanationType] = useState<string>("aggregate");
  const [isGraphLoaded, setIsGraphLoaded] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [isDateLoading, setIsDateLoading] = useState(true);

  const [isEventModalOpen, setIsEventModalOpen] = useState(false);
  const [selectedEventContext, setSelectedEventContext] = useState<{ name: string, date: string, endDate?: string } | null>(null);

  // useEffect(() => {
  //   const fetchedDate = (latestDateResponse as any)?.data?.latest_date;
  //   if (fetchedDate) {
  //     setSelectedDate(new Date(fetchedDate));
  //   } else if (!selectedDate) {
  //     setSelectedDate(new Date('2026-06-27'));
  //   }
  // }, [latestDateResponse]);

  useEffect(() => {
    const fetchedDate = (latestDateResponse as any)?.data?.latest_date;

    if (fetchedDate) {
      setSelectedDate(new Date(fetchedDate));
      setIsDateLoading(false);
    } else if (latestDateResponse) {
      // API completed but no date returned
      setSelectedDate(new Date());
      setIsDateLoading(false);
    }
  }, [latestDateResponse]);

  useEffect(() => {
    if (propertyCode && selectedPropertyCodes.length === 0) {
      setSelectedPropertyCodes([String(propertyCode)]);
    }
  }, [propertyCode]);

  const selectedProperties = useMemo(() => {
    return allProperties.filter(p => selectedPropertyCodes.includes(p.code));
  }, [allProperties, selectedPropertyCodes]);

  useEffect(() => {
    setIsGraphLoaded(false);
  }, [selectedPropertyCodes, selectedDate, forecastType, selectedKpis, explanationType, selectedEventContext]);


  const handlePropertyChange = (event: any) => {
    const { target: { value } } = event;
    const newValues = typeof value === 'string' ? value.split(',') : value;
    if (newValues.length > 0) {
      setSelectedPropertyCodes(newValues);
    }
  };

  const handleKpiChange = (event: any) => {
    const { target: { value } } = event;
    setSelectedKpis(typeof value === "string" ? value.split(",") : value);
  };

  const handleGraphLoaded = useCallback((loaded: boolean) => {
    setIsGraphLoaded(loaded);
  }, []);

  const handleOpenEventModal = () => {
    if (selectedProperties.length === 0) {
      alert("Please select a property first to view its local events.");
      return;
    }
    setIsEventModalOpen(true);
  };

  const handleSelectEvent = (event: any, clickedDate: Date) => {
    setSelectedEventContext({
      name: event.name,
      date: event.startDate || event.date,
      endDate: event.endDate,
    });

    setSelectedDate(clickedDate);

    setIsEventModalOpen(false);
  };
  const handleGoBack = () => {
    setSelectedEventContext(null);

    const fetchedDate = (latestDateResponse as any)?.data?.latest_date;
    if (fetchedDate) {
      setSelectedDate(new Date(fetchedDate));
    } else {
      setSelectedDate(new Date());
    }
  };

  return (
    <Box className="dashBoardMain" sx={{ pt: -3, px: 2, pb: 2, width: "100%" }}>
      <Box
        sx={{
          p: { xs: 2, sm: 3 },
          width: "100%",
          backgroundColor: "#9E9E9E1A",
          borderRadius: "12px",
          margin: { xs: "0 auto" },
          maxWidth: { xs: "100%", sm: "none" },
        }}
      >
        <Box sx={{
          display: "flex",
          flexDirection: { xs: "column", lg: "row" },
          alignItems: { xs: "flex-start", lg: "center" },
          justifyContent: "space-between",
          gap: 2,
          mb: 3,
          pb: 2,
          borderBottom: "1px solid #e0e0e0"
        }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: "bold",
              mr: "auto",
              mt: { xs: 0, lg: 0.5 }
            }}
          >
            {selectedEventContext ? `Event Forecast: ${selectedEventContext.name}` : "Forecast"}
          </Typography>

          <Box sx={{
            display: "flex",
            gap: 1.5,
            alignItems: "center",
            flexWrap: 'wrap',
            justifyContent: { xs: "flex-start", lg: "flex-end" },
            width: { xs: "100%", lg: "auto" }
          }}>
            <Box sx={{ ...responsiveControlStyle, height: '40px', display: 'flex', alignItems: 'center' }}>
              <ChatDatePicker
                value={selectedDate}
                onChange={setSelectedDate}
                loading={isDateLoading}
              />
            </Box>

            <StyledSelect
              multiple
              value={selectedPropertyCodes}
              onChange={handlePropertyChange}
              loading={isPropertiesLoading}
              placeholder="Select Property"
              options={allProperties.map(p => ({
                value: p.code,
                label: p.name,
              }))}
              renderValue={(selected) => {
                if (!selected.length)
                  return <em>Select Property</em>;

                if (selected.length === 1) {
                  return (
                    allProperties.find(
                      p => p.code === selected[0]
                    )?.name
                  );
                }

                return `${selected.length} Properties`;
              }}
            />

            <StyledSelect
              value={forecastType}
              onChange={(e) => setForecastType(e.target.value)}
              options={[
                { value: "weekly", label: "Weekly" },
                { value: "monthly", label: "Monthly" },
              ]}
            />

            <StyledSelect
              value={explanationType}
              onChange={(e) => setExplanationType(e.target.value)}
              options={[
                { value: "aggregate", label: "Aggregate" },
                { value: "individual", label: "Individual" },
              ]}
            />

            <StyledSelect
              multiple
              value={selectedKpis}
              onChange={handleKpiChange}
              placeholder="Select KPI"
              options={KPI_OPTIONS.map(kpi => ({
                value: kpi,
                label: KPI_LABEL_MAP[kpi] ?? kpi,
              }))}
              renderValue={(selected) => {
                if (!selected.length)
                  return <em>Select KPI</em>;

                if (selected.length === KPI_OPTIONS.length)
                  return "All KPIs";

                return selected
                  .map((k: string | number) => KPI_LABEL_MAP[k] ?? k)
                  .join(", ");
              }}
            />

            {selectedEventContext && (
              <StyledButton
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={handleGoBack}
              >
                Standard Forecast
              </StyledButton>
            )}

            <StyledButton
              variant="contained"
              startIcon={<CalendarMonthIcon />}
              onClick={handleOpenEventModal}
            >
              Events
            </StyledButton>
          </Box>
        </Box>

        <Box>
          <PredictionGraph
            selectedProperties={selectedProperties}
            date={selectedDate}
            forecastType={forecastType}
            explanationType={explanationType}
            selectedKpis={selectedKpis}
            onDataLoaded={handleGraphLoaded}
            eventContext={selectedEventContext}
          />
        </Box>

        {/* Chatbot */}
        {isGraphLoaded && (
          <Fade in={isGraphLoaded}>
            <Box sx={{ mt: 4, pt: 2, borderTop: "1px solid #e0e0e0" }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Ask questions about this {selectedEventContext ? "event forecast" : "forecast"}
              </Typography>
              <PredictionChatbot
                selectedProperties={selectedProperties}
                currentContext={{
                  date: selectedDate,
                  forecastType: forecastType,
                  explanationType: explanationType,
                  kpis: selectedKpis,
                  eventContext: selectedEventContext
                }}
              />
            </Box>
          </Fade>
        )}
      </Box>

      <EventCalendarModal
        open={isEventModalOpen}
        onClose={() => setIsEventModalOpen(false)}
        targetProperty={selectedProperties[0]}
        initialDate={selectedDate}
        onSelectEvent={handleSelectEvent}
      />
    </Box>
  );
};

export default Forecast;